import { makeStyles } from "tss-react/mui";
import { ButtonBase as MuiButtonBase, Paper, useControlled, useFormControl } from "@mui/material";
import React from "react";
import { PaperSelectionControlProps } from "./paper-selection-control.types";
import { ParagraphBold } from "../typography";
import { grey } from "@mui/material/colors";

const useStyles = makeStyles<PaperSelectionControlProps>()((theme, props) => {
  const borderRadius = (theme.shape.borderRadius as number) * 2.5;

  return {
    root: {
      borderRadius: borderRadius,
      width: "100%",
      "&:active": {
        color: theme.palette.secondary.main,
        "& $icon": {
          color: theme.palette.secondary.main
        },
        "& $label": {
          color: theme.palette.text.primary
        },
        "& $paper": {
          borderColor: theme.palette.secondary.main
        }
      }
    },
    paper: {
      padding: theme.spacing(1),
      backgroundColor: grey[100],
      paddingBottom: theme.spacing(2),
      borderRadius: borderRadius,
      width: "100%",
      borderWidth: "3px",
      borderStyle: "solid",
      borderColor:
        props.checked && !props.disabled
          ? theme.palette.primary.main
          : props.checked && props.disabled
            ? theme.palette.secondary.main
            : theme.palette.common.white,
      transition: `border-color 0.1s ${theme.transitions.easing.easeInOut}`,
      position: "relative"
    },
    icon: {
      fontSize: theme.spacing(6),
      marginBottom: theme.spacing(2),
      marginTop: theme.spacing(3.5),
      transition: `color 0.1s ${theme.transitions.easing.easeInOut}`,
      color:
        props.checked && !props.disabled ? theme.palette.primary.main : theme.palette.secondary.main
    },
    label: {},
    control: {
      position: "absolute",
      top: 0,
      right: 0,
      "&.Mui-checked": {
        color: !props.disabled ? theme.palette.primary.main : theme.palette.secondary.main
      }
    }
  };
});

export const PaperSelectionControl: React.FC<
  React.PropsWithChildren<PaperSelectionControlProps>
> = (props) => {
  const {
    control,
    label,
    icon: IconComponent,
    autoFocus,
    checked: checkedProp,
    defaultChecked,
    disabled: disabledProp,
    name,
    onChange,
    required,
    tabIndex,
    value
  } = props;
  const [checked, setCheckedState] = useControlled({
    controlled: checkedProp,
    default: Boolean(defaultChecked),
    name: "PaperSelectionControl",
    state: "checked"
  });

  const controlRef = React.useRef<HTMLInputElement | null>(null);

  const muiFormControl = useFormControl();

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    // Workaround for https://github.com/facebook/react/issues/9023
    if (event.nativeEvent.defaultPrevented) {
      return;
    }

    const newChecked = event.target.checked;

    setCheckedState(newChecked);
    onChange?.(event, newChecked);
  };

  let disabled = disabledProp;

  if (muiFormControl) {
    if (typeof disabled === "undefined") {
      disabled = muiFormControl.disabled;
    }
  }

  const handleButtonClick = () => {
    if (controlRef.current) {
      controlRef.current.click();
    }
  };

  const { classes } = useStyles(props);
  const controlProps = {
    autoFocus,
    disabled,
    name,
    required,
    tabIndex,
    value,
    className: classes.control,
    onChange: handleInputChange,
    onClick: (e: any) => {
      e.stopPropagation();
    },
    inputRef: controlRef
  };
  return (
    <MuiButtonBase
      focusRipple
      className={classes.root}
      onClick={handleButtonClick}
      disabled={disabled}
    >
      <Paper elevation={0} className={classes.paper}>
        {React.createElement(control, controlProps)}
        <IconComponent color={checked ? "primary" : "secondary"} className={classes.icon} />
        <ParagraphBold>{label}</ParagraphBold>
      </Paper>
    </MuiButtonBase>
  );
};
